import type { TeoCore } from '../TeoCore';
import { param } from './actionString.helper';
import type { ActionParameter, ParametersToObject } from './actionString.types';

const actions = {
  openSubDimension: defineAction(
    [param.string('dimension')], //
    (teoCore, parameters) => {
      teoCore.activeText.ui.update('dimensionDrawerOpen', true);
      teoCore.activeText.ui.update('dimension', parameters.dimension);
    },
  ),
  openTextProperty: defineAction(
    [
      param.string('dimension'), //
      param.string('textProperty'),
    ],
    (teoCore, parameters) => {
      teoCore.activeText.ui.update('dimensionDrawerOpen', true);
      teoCore.activeText.ui.update('dimension', parameters.dimension);
      setTimeout(() => {
        teoCore.activeText.ui.update('textProperty', parameters.textProperty);
      }, 100);
    },
  ),
  setDimensionDrawerOpen: defineAction(
    [param.boolean('open')], //
    (core, params) => {
      core.activeText.ui.update('dimensionDrawerOpen', params.open);
    },
  ),
  help: defineAction(
    [
      param.string('topic'), //
      param.optionalString('section'),
      param.optionalString('content'),
    ],
    (core, params) => {
      console.log('help action', params);
      core.help.open(params);
    },
  ),
} as const;

export function isActionString(actionString: string): boolean {
  return actionString.startsWith('#teo:');
}

export function executeActionString(teoCore: TeoCore, actionString: string): void {
  try {
    // Parse the action string (format: #teo:actionName/param1/param2/...)
    const { actionName, paramValues, actionDef } = parseActionString(actionString);

    const params: Record<string, unknown> = {};

    for (const [i, def] of actionDef.parameters.entries()) {
      if (i >= paramValues.length) {
        if (def.type.endsWith('?')) {
          break;
        }
        throw new Error(`Missing required parameter for action: ${actionName} - ${def.name}`);
      }
      const value = paramValues[i];
      if (value === undefined) {
        // this is for typescript, it will always be set
        throw new Error(`Missing value for parameter for action: ${actionName} - ${def.name}`);
      }
      const name = def.name;
      const type = def.type;
      switch (type) {
        case 'string':
        case 'string?':
          params[name] = value;
          break;
        //  case 'number':
        //  case 'number?':
        //    params[name] = parseFloat(value);
        //    break;
        case 'boolean':
          // case 'boolean?':
          {
            if (value !== 'true' && value !== 'false') {
              throw new Error(`Invalid boolean value for parameter: ${name} - ${value}`);
            }
            params[name] = value === 'true';
          }
          break;
        default:
          throw new Error(`Unknown parameter type: ${type}`);
      }
    }
    console.log(`calling action: ${actionName} with params:`, params);
    actionDef.action(teoCore, params as never);
  } catch (error) {
    console.error('Error executing action:', error);
  }
}

function parseActionString(actionString: string) {
  if (!isActionString(actionString)) {
    throw new Error('Invalid action string format');
  }

  const parts = actionString.substring(5).split('/');
  const actionName = parts[0] as keyof typeof actions;
  const paramValues = parts.slice(1);

  const actionDef = actions[actionName];

  if (!actionDef) {
    throw new Error(`Unknown action: ${actionName}`);
  }
  return {
    actionName,
    paramValues,
    actionDef,
  };
}

function defineAction<const TParams extends ActionParameter[], TResult = void>(
  params: TParams,
  action: (teoCore: TeoCore, parameters: ParametersToObject<TParams>) => TResult,
) {
  // check if params are valid = optionals have to be at the end
  const optionalIndex = params.findIndex((param) => param.type.endsWith('?'));
  if (
    optionalIndex !== -1 &&
    params.slice(optionalIndex + 1).some((param) => !param.type.endsWith('?'))
  ) {
    throw new Error('Optional parameters must be at the end of the parameter list');
  }

  return {
    parameters: params,
    action,
  };
}
